import { useState } from "react";
import { m } from "framer-motion";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Confetti } from "../motion/Confetti";
import { useIntakeCopy } from "../i18n/useIntakeCopy";
import { DUR, EASE_ENTER, SPRING_TOUCH, staggerChildren, riseItem } from "../motion/tokens";

export function SuccessScreen({ artistName, trackingUrl, onAnother }: { artistName: string; trackingUrl: string; onAnother(): void }) {
  const { c, fmt } = useIntakeCopy();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(trackingUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* user can select the text */ }
  };
  return (
    <section className="relative py-6 text-center">
      <Confetti fire />
      <m.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={SPRING_TOUCH}
        className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[var(--color-primary)]">
        <Check className="h-8 w-8 text-[var(--color-bg)]" />
      </m.div>
      <h2 data-step-heading tabIndex={-1} className="mt-5 font-(family-name:--font-display) font-normal text-4xl text-[var(--color-text)] outline-none">{c.success.title}</h2>
      <p className="mx-auto mt-2 max-w-md text-[var(--color-text-muted)]">{fmt(c.success.body, { artist: artistName })}</p>

      <h3 className="mt-8 text-sm uppercase tracking-widest text-[var(--color-text-muted)]">{c.success.next}</h3>
      <m.ol variants={staggerChildren(0.18)} initial="hidden" animate="show" className="mx-auto mt-3 max-w-sm space-y-2 text-left">
        {c.success.steps.map((step, i) => (
          <m.li key={i} variants={riseItem} className="flex items-center gap-3">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[var(--color-primary)] font-(family-name:--font-display) font-normal text-[var(--color-primary)]">{i + 1}</span>
            <span className="text-[var(--color-text)]">{fmt(step, { artist: artistName })}</span>
          </m.li>
        ))}
      </m.ol>

      <m.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.7, duration: DUR.scene, ease: EASE_ENTER } }}
        className="mx-auto mt-8 max-w-lg rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
        <p className="text-xs text-[var(--color-text-muted)]">{c.success.trackingLabel}</p>
        <div className="mt-2 flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate text-left text-sm text-[var(--color-accent)]">{trackingUrl}</code>
          <Button type="button" size="sm" variant="secondary" onClick={copy}>
            {copied ? c.success.copied : <><Copy className="mr-1 h-3.5 w-3.5" />{c.success.copy}</>}
          </Button>
        </div>
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">{c.success.emailed}</p>
      </m.div>

      <div className="mt-8 flex justify-center gap-3">
        <Button variant="outline" onClick={() => { window.location.href = "/"; }}>{c.success.home}</Button>
        <Button onClick={onAnother}>{c.success.another}</Button>
      </div>
    </section>
  );
}
